import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { DOMAIN_LANGUAGE_CONFIG, DEFAULT_LANGUAGE, DOMAINS_HIDE_LANGUAGE_SELECTOR } from '../config/domain-language-config';

export interface Language {
  code: string;
  name: string;
  flag: string;
  locale: string;
  currency: string;
  dateFormat: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  // Domain to language mapping loaded from configuration
  private readonly domainLanguageMap: { [key: string]: string } = { ...DOMAIN_LANGUAGE_CONFIG };
  public readonly languages: Language[] = [
    {
      code: 'en-US',
      name: 'English (US)',
      flag: 'us',
      locale: 'en-US',
      currency: 'USD',
      dateFormat: 'MM/dd/yyyy'
    },
    {
      code: 'en-GB',
      name: 'English (UK)',
      flag: 'gb',
      locale: 'en-GB',
      currency: 'GBP',
      dateFormat: 'dd/MM/yyyy'
    },
    {
      code: 'de-DE',
      name: 'Deutsch',
      flag: 'de',
      locale: 'de-DE',
      currency: 'EUR',
      dateFormat: 'dd.MM.yyyy'
    },
    {
      code: 'fr-FR',
      name: 'Français',
      flag: 'fr',
      locale: 'fr-FR',
      currency: 'EUR',
      dateFormat: 'dd/MM/yyyy'
    },
    {
      code: 'es-ES',
      name: 'Español',
      flag: 'es',
      locale: 'es-ES',
      currency: 'EUR',
      dateFormat: 'dd/MM/yyyy'
    },
    {
      code: 'it-IT',
      name: 'Italiano',
      flag: 'it',
      locale: 'it-IT',
      currency: 'EUR',
      dateFormat: 'dd/MM/yyyy'
    }
  ];

  private currentLanguageSubject = new BehaviorSubject<Language>(this.languages[0]);
  public currentLanguage$ = this.currentLanguageSubject.asObservable();

  constructor(private translate: TranslateService) {
    this.initializeTranslateService();
    this.initializeLanguage();
  }

  private initializeTranslateService(): void {
    // Set available languages
    this.translate.addLangs(['en', 'en-US', 'en-GB', 'de-DE', 'fr-FR', 'es-ES', 'it-IT']);
    
    // Set default language
    this.translate.setDefaultLang('en');
  }

  private initializeLanguage(): void {
    // 1. First check for user override (takes precedence over URL-based selection)
    const userOverride = localStorage.getItem('userLanguageOverride');
    if (userOverride) {
      const language = this.languages.find(l => l.code === userOverride);
      if (language) {
        this.setLanguage(language.code, false);
        return;
      }
    }

    // 2. Check for URL-based language selection
    const urlLanguage = this.getLanguageFromURL();
    if (urlLanguage) {
      this.setLanguage(urlLanguage, false); // Don't save to localStorage for URL-based selection
      return;
    }

    // 3. Fallback to saved language in localStorage
    const savedLang = localStorage.getItem('selectedLanguage');
    if (savedLang) {
      const language = this.languages.find(l => l.code === savedLang);
      if (language) {
        this.setLanguage(language.code, false);
        return;
      }
    }

    // 4. Default to configured default language
    const defaultLang = this.languages.find(l => l.code === DEFAULT_LANGUAGE) || this.languages[0];
    this.setLanguage(defaultLang.code, false);
  }

  private getLanguageFromURL(): string | null {
    const hostname = window.location.hostname;
    const mappedLanguage = this.domainLanguageMap[hostname];
    
    // Log for debugging (can be removed in production)
    if (mappedLanguage) {
      console.log(`Language auto-selected from URL: ${hostname} -> ${mappedLanguage}`);
    }
    
    return mappedLanguage || null;
  }

  private setLanguage(languageCode: string, saveToStorage: boolean = true): void {
    const language = this.languages.find(l => l.code === languageCode);
    if (language) {
      this.translate.use(language.code);
      this.currentLanguageSubject.next(language);
      
      if (saveToStorage) {
        localStorage.setItem('selectedLanguage', language.code);
      }
    }
  }

  public changeLanguage(languageCode: string): void {
    // Always save user language selection as an override
    // This takes precedence over URL-based language detection
    localStorage.setItem('userLanguageOverride', languageCode);
    this.setLanguage(languageCode, false);
    
    // Reload the page to apply locale changes
    window.location.reload();
  }

  public getCurrentLanguage(): Language {
    return this.currentLanguageSubject.value;
  }

  public getLanguageByCode(code: string): Language | undefined {
    return this.languages.find(l => l.code === code);
  }

  public getCurrentLocale(): string {
    return this.currentLanguageSubject.value.locale;
  }

  public getCurrentCurrency(): string {
    return this.currentLanguageSubject.value.currency;
  }

  public getDateFormat(): string {
    return this.currentLanguageSubject.value.dateFormat;
  }

  public getShortDateFormat(): string {
    const language = this.currentLanguageSubject.value;
    switch (language.code) {
      case 'en-GB':
        return 'dd/MM/y';
      case 'de-DE':
        return 'dd.MM.y';
      case 'fr-FR':
      case 'es-ES':
      case 'it-IT':
        return 'dd/MM/y';
      default:
        return 'dd/MM/y';
    }
  }

  public getDomainLanguageMap(): { [key: string]: string } {
    return { ...this.domainLanguageMap };
  }

  public addDomainMapping(domain: string, languageCode: string): void {
    this.domainLanguageMap[domain] = languageCode;
  }

  public removeDomainMapping(domain: string): void {
    delete this.domainLanguageMap[domain];
  }

  public isUrlBasedLanguage(): boolean {
    return this.getLanguageFromURL() !== null;
  }

  public hasUserLanguageOverride(): boolean {
    return localStorage.getItem('userLanguageOverride') !== null;
  }

  public clearUserLanguageOverride(): void {
    localStorage.removeItem('userLanguageOverride');
    // Reinitialize language selection without the override
    this.initializeLanguage();
  }

  public shouldHideLanguageSelector(): boolean {
    const hostname = window.location.hostname;
    return DOMAINS_HIDE_LANGUAGE_SELECTOR.includes(hostname);
  }
}